import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  Form,
  FormButton,
  Input,
  string,
  SwitchInput,
  useNotifications
} from '@ijoin/components';
import { usePatchAdminLogin } from '@ijoin/hooks';
import { InfoOutlined } from '@mui/icons-material';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack
} from '@mui/material';

import { useForm } from 'react-hook-form';
import { z } from 'zod';

type EditDetailsDialogProps = {
  email?: string;
  firstName?: string;
  isAdmin?: boolean;
  isMfaRequired: boolean;
  lastName?: string;
  loginId: string;
  onClose: () => void;
  onSuccess: () => void;
  use2fa: boolean;
};

const editDetailsResolver = zodResolver(
  z.object({
    email: string(),
    firstName: string(),
    isSuperAdmin: z.boolean(),
    lastName: string(),
    use2fa: z.boolean()
  })
);

export const EditDetailsDialog = (props: EditDetailsDialogProps) => {
  const notifications = useNotifications();

  const methods = useForm({
    defaultValues: {
      email: props.email ?? '',
      firstName: props.firstName ?? '',
      isSuperAdmin: props.isAdmin ?? false,
      lastName: props.lastName ?? '',
      use2fa: props.use2fa || !!props.isAdmin
    },
    mode: 'all',
    resolver: editDetailsResolver
  });

  const updateAdmin = usePatchAdminLogin({
    mutation: {
      onError: () => notifications.error('Failed to update details'),
      onSuccess: () => {
        props.onClose();
        props.onSuccess();
        notifications.success('Details updated');
      }
    }
  });

  const onSuperAdminChange = (isSuperAdmin: boolean) => {
    if (isSuperAdmin) {
      methods.setValue('use2fa', true);
    }
  };

  const onSubmit = (data: {
    email: string;
    firstName: string;
    isSuperAdmin: boolean;
    lastName: string;
    use2fa: boolean;
  }) => {
    updateAdmin.mutate({
      data: {
        email: data.email || undefined,
        firstName: data.firstName || undefined,
        isAdmin: data.isSuperAdmin,
        lastName: data.lastName || undefined,
        use2fa: data.use2fa
      },
      loginId: props.loginId
    });
  };

  const isSuperAdmin = methods.watch('isSuperAdmin');

  return (
    <Dialog
      data-testid='editDetailsDialog'
      fullWidth
      isOpen
      maxWidth='sm'
      onClose={props.onClose}>
      <Form
        methods={methods}
        noValidate
        onSubmit={methods.handleSubmit(onSubmit)}>
        <DialogTitle variant='h3'>Edit Details</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Input
              data-1p-ignore
              data-testid='editFirstName'
              format='string'
              fullWidth
              hideError
              label='First Name'
              name='firstName'
              size='small'
            />
            <Input
              data-1p-ignore
              data-testid='editLastName'
              format='string'
              fullWidth
              hideError
              label='Last Name'
              name='lastName'
              size='small'
            />
            <Input
              data-1p-ignore
              data-testid='editEmail'
              format='string'
              fullWidth
              hideError
              label='Email'
              name='email'
              size='small'
            />
            <SwitchInput
              color='success'
              data-testid='editSuperAdmin'
              hideError
              label='Super Admin'
              name='isSuperAdmin'
              onChange={onSuperAdminChange}
            />
            <SwitchInput
              color='success'
              data-testid='editUse2fa'
              disabled={props.isMfaRequired || isSuperAdmin}
              hideError
              label='2FA Enabled'
              name='use2fa'
              tooltip={
                props.isMfaRequired
                  ? 'Required while Can View Sensitive Data is enabled.'
                  : isSuperAdmin
                    ? 'Required for super admins.'
                    : 'When turned on, the user sets up an authenticator app at their next login.'
              }
              tooltipIcon={<InfoOutlined />}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid='cancelEditDetails'
            onClick={props.onClose}
            variant='outlined'>
            Cancel
          </Button>
          <FormButton
            data-testid='saveEditDetails'
            triggerValidation
            variant='contained'>
            Save
          </FormButton>
        </DialogActions>
      </Form>
    </Dialog>
  );
};
